import type { AxiosInstance } from 'axios';
import type {
  CallReportCompanyUsageRequest,
  CallReportCompanyUsageResponse,
  ContactHistoryResponse,
  ListContactHistoryRequest,
} from './contracts';

/**
 * Gateway for interacting with the Call Report API.
 */
export class CallReportGateway {
  constructor(
    private readonly httpClient: AxiosInstance,
    private readonly baseUrl: string,
  ) {}

  /**
   * Lists contact interaction history across chat and CDR indexes.
   * @param params - Optional filters, pagination, and index selection.
   * @returns The paginated contact history response.
   */
  async listContactHistory(params: ListContactHistoryRequest = {}): Promise<ContactHistoryResponse> {
    const { data } = await this.httpClient.get<ContactHistoryResponse>(`${this.baseUrl}/history/index/multi`, {
      params,
    });
    return data;
  }

  /**
   * Fetches aggregated telephony usage for a company.
   * @param companyId - The company identifier.
   * @param params - Optional date range filters.
   * @returns The aggregated telephony usage totals.
   */
  async getCompanyUsage(
    companyId: string,
    params: CallReportCompanyUsageRequest = {},
  ): Promise<CallReportCompanyUsageResponse> {
    const { data } = await this.httpClient.get<CallReportCompanyUsageResponse>(
      `${this.baseUrl}/companies/${companyId}/usage`,
      { params },
    );
    return data;
  }
}

export type {
  CallReportCompanyUsageRequest,
  CallReportCompanyUsageResponse,
  ContactHistoryIndex,
  ContactHistoryResponse,
  ContactHistorySortOrder,
  CallReportTelephonyUsageEntryResponse,
  CallReportTelephonyUsageProvidersResponse,
  CallReportTelephonyUsageResponse,
  ContactInteractionResponse,
  InteractionCallStepResponse,
  InteractionChatStepResponse,
  InteractionMemberResponse,
  InteractionOrganizationResponse,
  ListContactHistoryRequest,
} from './contracts';
